import { Component, OnInit, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { getCustomerConfigState } from '../../../../redux/reducers';
import { RequestCreditLineStatus } from '../../redux/actions/actions-avc-credit-line';
import {
  selectCreditLineStatus,
  selectCreditLineStatusError,
  selectCreditLineStatusLoading
} from '../../redux/selectors/avc-credit-line-status-selector';
import { CreditLineAgency, ReadCreditLineStatusResponse } from '../../redux/services/avc-credit-line-status.model';
import { CreditLineChartInput } from '../avc-credit-line-chart/avc-credit-line-chart.component';
import { isOn } from 'feature-toggle-service';
import { Subscription } from 'rxjs';
import { customerConfig } from '@uxdf/direct-shared-lib';

@Component({
  standalone: false,
  selector: 'app-avc-credit-line',
  templateUrl: './avc-credit-line.component.html',
  styleUrls: ['./avc-credit-line.component.scss']
})
export class AvcCreditLineComponent implements OnInit, OnDestroy {
  readonly INCONSISTENT_AGENCY_DATA_ERROR = 'Inconsistent agency data';
  creditLineStatus: ReadCreditLineStatusResponse;
  viewAvcCreditLine = false;
  creditLineChartInput: CreditLineChartInput[];
  errorMessage = '';
  errorMessageChild = '';
  inlineErrorMessage = '';
  inlineErrorMessageChild = '';
  creditLineStatusLoading$;
  totalGrantedAllOffices = $localize`:@@amadeus-direct.avc-credit-line.total-granted:Total granted for all offices:`;


  private readonly creditLineAgenciesStatusMap: Map<string, CreditLineAgency> = new Map();
  selectedAgency: { value: string; label: string };
  agenciesList: { value: string; label: string }[] = [];

  private readonly creditLineOwnersStatusMap: Map<string, CreditLineAgency> = new Map();
  private selectedCreditLineOwner: { value: string; label: string };
  private readonly creditLineOwnersList: { value: string; label: string }[] = [];
  isSnow = false;
  private customerConfigSubscription: Subscription;

  readonly creditlineS3jEnabled = isOn('AD_AVC_CREDIT_LINE_S3J_A');

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    this.store
      .select(getCustomerConfigState)
      .pipe(take(1))
      .subscribe(config => {
        this.creditLineStatusLoading$ = this.store.select(selectCreditLineStatusLoading);

        this.subscribeToCreditLineStatus(config);

        this.store.select(selectCreditLineStatusError).subscribe(creditLineStatusError => {
          if (creditLineStatusError) {
            this.errorMessage = $localize`:@@amadeus-direct.avc-credit-line.errors.technical-error:There was a technical error.`;
          }
        });
      });
    this.store.select(getCustomerConfigState).subscribe(config => {
      this.viewAvcCreditLine = config.customerConfig?.is_avc_credit_line_status;
    });

    this.customerConfigSubscription = this.store.pipe(
        select(customerConfig)).subscribe(response => {
            this.isSnow = response.customerConfig.is_snow;
        });
  }

  subscribeToCreditLineStatus(config): void {
    this.store.select(selectCreditLineStatus).subscribe(creditLineStatus => {
      if (!creditLineStatus) {
        this.dispatchCreditLineStatusRequest(config);
      } else {
        this.handleCreditLineStatus(creditLineStatus);
      }
    });
  }

  dispatchCreditLineStatusRequest(config): void {
    this.store.dispatch(new RequestCreditLineStatus());
  }

  isValidScenario(): boolean {
    const agencyData = this.creditLineStatus.body.agencyData;
    const hasNoAgencyData: boolean = this.creditlineS3jEnabled
      ? agencyData?.isRegistered === false
      : agencyData == null;

    if (this.creditLineStatus.statusCodeValue === 400) {
      this.errorMessage = $localize`:@@amadeus-direct.avc-credit-line.errors.no-data-for-office:Data not found for this office ID.`;
    } else if (this.creditLineStatus.statusCodeValue === 401 || this.creditLineStatus.statusCodeValue === 403) {
      this.errorMessage = $localize`:@@amadeus-direct.avc-credit-line.errors.technical-error:There was a technical error.`;
    } else if (
      this.creditLineStatus.statusCodeValue === 500 &&
      this.creditLineStatus.body?.errorMessage !== this.INCONSISTENT_AGENCY_DATA_ERROR
    ) {
      this.errorMessage = $localize`:@@amadeus-direct.avc-credit-line.errors.technical-error:There was a technical error.`;
    } else if (this.creditLineStatus.body?.errorMessage === this.INCONSISTENT_AGENCY_DATA_ERROR) {
      this.errorMessage = $localize`:@@amadeus-direct.avc-credit-line.errors.no-credit-line-data:Credit line data not available.`;
    } else if (hasNoAgencyData) {
      this.errorMessage = $localize`:@@amadeus-direct.avc-credit-line.errors.no-data-for-office:Data not found for this office ID.`;
    } else if (
      !this.creditLineStatus.body.agencyData.isCreditLineCustomer ||
      !this.creditLineStatus.body.agencyData.isCreditLineActivated
    ) {
      this.errorMessage = $localize`:@@amadeus-direct.avc-credit-line.errors.no-credit-line-data:Credit line data not available.`;
    }

    return this.errorMessage === '';
  }

  private populateAllAgenciesData(): void {
    if (this.loggedInUserIsCreditLineOwner()) {
      this.populateChildAgenciesForCreditLineOwner();
    } else if (this.loggedInUserIsAdministrativeHead()) {
      this.populateCreditLineOwnersForAdministrativeHead();
      this.populateChildAgenciesForAdministrativeHead();
    } else {
      this.populateSingleChildAgency();
    }
  }

  private populateSingleChildAgency() {
    this.creditLineAgenciesStatusMap.set(
      this.creditLineStatus.body.requestedOfficeId,
      this.creditLineStatus.body.agencyData
    );
    this.agenciesList.push({
      value: this.creditLineStatus.body.requestedOfficeId,
      label: this.getAgencyNameOfficeId(this.creditLineStatus.body.agencyData)
    });
  }

  private populateChildAgenciesForAdministrativeHead() {
    this.agenciesList.push(this.selectedCreditLineOwner);
    this.creditLineAgenciesStatusMap.set(
      this.selectedCreditLineOwner.value,
      this.creditLineOwnersStatusMap.get(this.selectedCreditLineOwner.value)
    );

    for (const subAgency of this.creditLineStatus.body.agencyData.subAgencies) {
      for (const subSubAgency of subAgency.subAgencies) {
        this.creditLineAgenciesStatusMap.set(subSubAgency.officeId, subSubAgency);
        if (this.getSelectedCreditLineOwner().officeId === subAgency.officeId) {
          this.agenciesList.push({ value: subSubAgency.officeId, label: this.getAgencyNameOfficeId(subSubAgency) });
        }
      }
    }
  }

  private populateCreditLineOwnersForAdministrativeHead() {
    for (const subAgency of this.creditLineStatus.body.agencyData.subAgencies) {
      this.creditLineOwnersStatusMap.set(subAgency.officeId, subAgency);
      this.creditLineOwnersList.push({ value: subAgency.officeId, label: this.getAgencyNameOfficeId(subAgency) });

      if (subAgency.officeId === this.creditLineStatus.body.requestedOfficeId) {
        this.selectedCreditLineOwner = {
          value: subAgency.officeId,
          label: this.getAgencyNameOfficeId(subAgency)
        };
      }
    }
    if (this.selectedCreditLineOwner == null) {
      this.selectedCreditLineOwner = this.creditLineOwnersList[0];
    }
  }

  private populateChildAgenciesForCreditLineOwner() {
    this.creditLineAgenciesStatusMap.set(
      this.creditLineStatus.body.requestedOfficeId,
      this.creditLineStatus.body.agencyData
    );
    this.agenciesList.push({
      value: this.creditLineStatus.body.requestedOfficeId,
      label: this.getAgencyNameOfficeId(this.creditLineStatus.body.agencyData)
    });
    for (const subAgency of this.creditLineStatus.body.agencyData.subAgencies) {
      this.creditLineAgenciesStatusMap.set(subAgency.officeId, subAgency);
      this.agenciesList.push({ value: subAgency.officeId, label: this.getAgencyNameOfficeId(subAgency) });
    }
  }

  handleCreditLineStatus(creditLineStatus): void {
    this.agenciesList = [];
    this.creditLineChartInput = null;
    this.creditLineStatus = creditLineStatus;
    this.selectedCreditLineOwner = null;
    if (this.isValidScenario()) {
      this.populateAllAgenciesData();

      this.selectedAgency = this.agenciesList[0];

      if (this.isNoNetworkAgency() || this.loggedInUserIsCreditLineOwner() || this.loggedInUserIsAdministrativeHead()) {
        this.checkIfAmountsAreValid();
        this.createCreditLineChartInput();
      }
    }
  }

  displayAsChild(): boolean {
    return (
      (this.creditLineStatus.body.agencyData.hierarchyLevel === 1 ||
        this.creditLineStatus.body.agencyData.hierarchyLevel === 2) &&
      this.selectedAgency &&
      this.getSelectedAgency().officeId === this.selectedAgency.value
    );
  }

  private checkIfAmountsAreValid(): void {
    let error = '';

    if (this.availableAmountIsNegative()) {
      error = $localize`:@@amadeus-direct.avc-credit-line.errors.negative-amount:Chart not available due to a negative amount.`;
    } else if (this.initialAmountIsZero()) {
      error = $localize`:@@amadeus-direct.avc-credit-line.errors.no-initial-amount:Chart not available due to no granted amount.`;
    } else if (this.usedPercentageIsNegative()) {
      error = $localize`:@@amadeus-direct.avc-credit-line.errors.negative-percentage:Chart not available due to a negative percentage.`;
    }

    if (this.displayAsChild()) {
      this.inlineErrorMessageChild = error;
    } else {
      this.inlineErrorMessage = error;
    }
  }

  private checkIfCreditLineIsActive(): boolean {
    let error = '';
    const creditLineAgency = this.getSelectedAgency();

    if (!creditLineAgency.isCreditLineCustomer || !creditLineAgency.isCreditLineActivated) {
      error = $localize`:@@amadeus-direct.avc-credit-line.errors.no-credit-line-data:Credit line data not available.`;
    }

    if (this.displayAsChild()) {
      this.errorMessageChild = error;
    } else {
      this.errorMessage = error;
    }

    return error === '';
  }

  getTotalGrantedForAllOffices(): number {
    if (this.loggedInUserIsCreditLineOwner()) {
      return this.getTotalGrantedForAllOfficesCurrentAgencyIsLevelTwoParent();
    }

    if (this.loggedInUserIsAdministrativeHead()) {
      return this.getTotalGrantedForAllOfficesCurrentAgencyIsLevelThreeParent();
    }

    return 0;
  }

  private getTotalGrantedForAllOfficesCurrentAgencyIsLevelThreeParent(): number {
    let totalGrantedEuro = 0;

    const selectedCreditLineOwner = this.creditLineOwnersStatusMap.get(this.selectedCreditLineOwner.value);

    const euroToCurrentCurrencyConversionRatio =
      selectedCreditLineOwner.initialAmount / selectedCreditLineOwner.initialAmountEUR;
    for (const creditLineOwner of this.creditLineOwnersStatusMap.values()) {
      if (
        creditLineOwner.initialAmount !== 0 &&
        creditLineOwner.isCreditLineActivated &&
        creditLineOwner.isCreditLineCustomer
      ) {
        totalGrantedEuro += creditLineOwner.initialAmountEUR;
      }
    }

    return totalGrantedEuro * euroToCurrentCurrencyConversionRatio;
  }

  private getTotalGrantedForAllOfficesCurrentAgencyIsLevelTwoParent(): number {
    const agencyData = this.creditLineStatus.body.agencyData;
    if (agencyData.creditLineModel === 'Shared') {
      return agencyData.initialAmount;
    }

    let totalGrantedEuro = 0;
    if (agencyData.initialAmount === 0 || !agencyData.isCreditLineActivated || !agencyData.isCreditLineCustomer) {
      return 0;
    }

    const euroToCurrentCurrencyConversionRatio = agencyData.initialAmount / agencyData.initialAmountEUR;
    for (const creditLineAgency of this.creditLineAgenciesStatusMap.values()) {
      if (
        creditLineAgency.initialAmount !== 0 &&
        creditLineAgency.isCreditLineActivated &&
        creditLineAgency.isCreditLineCustomer
      ) {
        totalGrantedEuro += creditLineAgency.initialAmountEUR;
      }
    }

    return totalGrantedEuro * euroToCurrentCurrencyConversionRatio;
  }

  getSelectedAgency(): CreditLineAgency {
    const selectedAgency = this.creditLineAgenciesStatusMap.get(this.selectedAgency.value);
    return selectedAgency ? selectedAgency : this.creditLineStatus.body.agencyData;
  }

  getSelectedCreditLineOwner(): CreditLineAgency {
    return this.creditLineOwnersStatusMap.get(this.selectedCreditLineOwner.value);
  }

  isNoNetworkAgency(): boolean {
    const creditLineAgency = this.creditLineStatus.body.agencyData;
    return creditLineAgency.hierarchyLevel === 0 && creditLineAgency.subAgencies.length === 0;
  }

  loggedInUserIsCreditLineOwner(): boolean {
    const creditLineAgency = this.creditLineStatus.body.agencyData;
    return creditLineAgency.hierarchyLevel === 2;
  }

  isChildAgency(creditLineAgency = this.creditLineStatus.body.agencyData): boolean {
    return (
      creditLineAgency.officeId !== this.creditLineStatus.body.agencyData.officeId ||
      (creditLineAgency.hierarchyLevel === 3 && creditLineAgency.subAgencies.length === 0)
    );
  }

  loggedInUserIsAdministrativeHead(): boolean {
    const creditLineAgency = this.creditLineStatus.body.agencyData;
    return creditLineAgency.hierarchyLevel === 1 && creditLineAgency.creditLineModel === 'Split';
  }

  getCurrentAgencyNameOfficeId(): string {
    const creditLineAgency = this.creditLineStatus.body.agencyData;

    return `${creditLineAgency.companyName} (${creditLineAgency.officeId})`;
  }

  getAgencyNameOfficeId(creditLineAgency: CreditLineAgency): string {
    return `${creditLineAgency.companyName} (${creditLineAgency.officeId})`;
  }

  getAgencyNameOfficeIdList(): { value: string; label: string }[] {
    const result = [];
    for (const [, creditLineAgency] of this.creditLineAgenciesStatusMap.entries()) {
      result.push({
        value: creditLineAgency.officeId,
        label: `${creditLineAgency.companyName} (${creditLineAgency.officeId})`
      });
    }

    return result;
  }

  showChart(): boolean {
    return (
      !this.availableAmountIsNegative() &&
      !this.usedPercentageIsNegative() &&
      !this.initialAmountIsZero() &&
      !this.errorMessageChild &&
      !this.inlineErrorMessageChild
    );
  }

  availableAmountIsNegative(): boolean {
    return this.getSelectedAgency().openAmount < 0;
  }

  initialAmountIsZero(): boolean {
    return this.getSelectedAgency().initialAmount === 0;
  }

  usedPercentageIsNegative(): boolean {
    return this.getSelectedAgency().percentageUsed < 0;
  }

  createCreditLineChartInput(): void {
    const creditLineAgency = this.getSelectedAgency();
    this.creditLineChartInput = [
      {
        amount: creditLineAgency.usedAmount,
        currency: creditLineAgency.salesCurrency,
        label: $localize`:@@amadeus-direct.avc-credit-line.spent:Spent`
      },
      {
        amount: creditLineAgency.openAmount,
        currency: creditLineAgency.salesCurrency,
        label: $localize`:@@amadeus-direct.avc-credit-line.available:Available`
      }
    ];
  }

  onAgencyChange() {
    const creditLineActive = this.checkIfCreditLineIsActive();
    if (creditLineActive) {
      this.checkIfAmountsAreValid();
    }

    if (!this.errorMessage && !this.inlineErrorMessage) {
      this.createCreditLineChartInput();
    }
  }

  onCreditLineOwnerChange() {
    this.agenciesList = [];

    const creditLineOwner = this.getSelectedCreditLineOwner();

    this.creditLineAgenciesStatusMap.set(
      this.selectedCreditLineOwner.value,
      this.creditLineOwnersStatusMap.get(this.selectedCreditLineOwner.value)
    );

    this.agenciesList.push(this.selectedCreditLineOwner);

    for (const subAgency of creditLineOwner.subAgencies) {
      this.creditLineAgenciesStatusMap.set(subAgency.officeId, subAgency);
      this.agenciesList.push({ value: subAgency.officeId, label: this.getAgencyNameOfficeId(subAgency) });
    }

    this.selectedAgency = this.agenciesList[0];
    this.onAgencyChange();
  }

  
  ngOnDestroy() {
    if (this.customerConfigSubscription) {
      this.customerConfigSubscription.unsubscribe();
    }
  }

}
